import { EmptyArray } from 'lib/array/arrayUtils'
import { setAttribute } from 'lib/object/objectUtils'
import createResponsiveClassNameGetter from 'lib/styles/createResponsiveClassNameGetter'
import type { ExecutionContext } from 'styled-components'
import { css } from 'styled-components'

export const getThemeClassName = createResponsiveClassNameGetter<App.Design.PaletteType>('app-theme')

export function makeCSSVar(...name: Array<string>): App.Design.CSSVar {
  const kebabCasedName = name.map(s => s.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/[\s_]+/g, '-').toLowerCase())
  return `--${kebabCasedName.join('-')}`
}

function isCSSVar(value: string): value is App.Design.CSSVar {
  return value.startsWith('--')
}

export function readCSSVar(
  cssVar: App.Design.CSSVar,
  /**
   * _**NOTE!**_:
   * Only CSSVar type fallbacks will chain recursively.
   * As soon as the first non-CSSVar is detected, the chaining will stop.
   */
  ...fallbacks: Array<App.Design.CSSVar | string>
): App.Design.CSSVarValue {
  if (!fallbacks.length) {
    return `var(${cssVar})`
  }

  const [fallback, ...restOfFallbacks] = fallbacks
  if (isCSSVar(fallback)) {
    return `var(${cssVar}, ${readCSSVar(fallback, ...restOfFallbacks)})`
  }

  return `var(${cssVar}, ${fallback})`
}

export function setCSSVar(
  cssVar: App.Design.CSSVar,
  value: string | ((props: ExecutionContext) => string),
) {
  return css({ [cssVar]: value })
}

const defaultCSSVarValueMapper = (value: unknown) => String(value)

export function generateCSSVarEntries(
  valuesRecord: Record<string, unknown> | object,
  /**
   * Will be kebab-case
   */
  varKeyInitialDepth: string | Array<string> = EmptyArray,
  valueMapper: (value: unknown, keyDepth: Array<string>) => string = defaultCSSVarValueMapper,
): Array<[cssVar: App.Design.CSSVar, value: string]> {
  const entries: Array<[cssVar: App.Design.CSSVar, value: string]> = []

  if (Array.isArray(valuesRecord)) return entries

  for (const [key, value] of Object.entries(valuesRecord)) {
    if (value === undefined || value === null || value === Array.isArray(value)) {
      continue
    }

    const inferredDepth = Array.isArray(varKeyInitialDepth) ? [...varKeyInitialDepth, key] : [varKeyInitialDepth, key]
    if (typeof value === 'string' || typeof value === 'number') {
      entries.push([makeCSSVar(...inferredDepth), valueMapper(value, inferredDepth)])
    }
    if (typeof value === 'object') {
      entries.push(...generateCSSVarEntries(value, inferredDepth, valueMapper))
    }
  }
  return entries
}

export function mapObjectToCSSVarValues<Input extends Record<string, unknown> | object, Output extends Input>(
  valuesRecord: Input,
  /**
   * Will be kebab-case
   */
  varKeyInitialDepth: string | Array<string> = EmptyArray,
): Output {
  const mappedObject: Output = {} as Output

  if (Array.isArray(valuesRecord)) return mappedObject

  for (const [key, value] of Object.entries(valuesRecord)) {
    if (Array.isArray(value)) {
      continue
    }

    const inferredDepth = Array.isArray(varKeyInitialDepth) ? [...varKeyInitialDepth, key] : [varKeyInitialDepth, key]
    if (typeof value === 'object') {
      setAttribute(mappedObject, key, mapObjectToCSSVarValues(value, inferredDepth))
    } else {
      setAttribute(mappedObject, key, readCSSVar(makeCSSVar(...inferredDepth)))
    }
  }

  return mappedObject
}
